/**
 * Materials Database Service
 * Firebase CRUD operations for timber and hardware items with real-time sync
 */

import { db } from './firebase';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    orderBy,
    writeBatch,
    Timestamp,
    Unsubscribe,
} from 'firebase/firestore';
import {
    applySupplierPriceWithHistory,
    normalizePriceHistory,
    toHistoryDate,
} from './timberSupplierPriceHistory';
import {
    applyHardwareSupplierPriceWithHistory,
    normalizeHardwarePriceHistory,
    toHardwareHistoryDate,
} from './hardwareSupplierPriceHistory';
import {
  canonicalizeGrade,
} from './timberGradeCanonical';

export {
  canonicalizeGrade,
  gradesEquivalent,
  uniqueCanonicalGrades,
} from './timberGradeCanonical';

// Types
export type TimberPriceHistorySource =
  | 'manual'
  | 'import'
  | 'materials_price_approve'
  | 'seed';

export interface SupplierPricePoint {
  piecePrice: number;
  costPerBdFt: number;
  recordedAt: Date;
  source?: TimberPriceHistorySource | string;
}

export interface SupplierPrice {
  supplier: string;
  piecePrice: number;
  costPerBdFt: number;
  lastQuoteDate?: Date;
  notes?: string;
  /** Oldest → newest; live piecePrice/costPerBdFt should match the last point. */
  priceHistory?: SupplierPricePoint[];
}

/** Green vs kiln dried; empty/omitted means unspecified (legacy rows). */
export type TimberMoistureContent = '' | 'green' | 'kiln_dried';

export interface TimberItem {
    id: string;
    species: string;
    grade: string;
    dimensions: string;
    /** Green, kiln dried, or omitted/empty for unspecified */
    moistureContent?: TimberMoistureContent;
    piecePrice: number;           // active price (from starred supplier)
    costPerBdFt: number;
    boardFeet: number;
    supplier?: string;            // active supplier name (for display)
    supplierPricing?: SupplierPrice[];  // all supplier prices
    createdAt: Date;
    updatedAt: Date;
}

export interface TimberItemInput {
    species: string;
    grade: string;
    dimensions: string;
    moistureContent?: TimberMoistureContent;
    piecePrice: number;
    costPerBdFt: number;
    boardFeet: number;
    supplier?: string;
    supplierPricing?: SupplierPrice[];
}

// Collection reference
const TIMBER_COLLECTION = 'materialsDatabase_timber';

/** Convert Firestore Timestamp or plain {seconds, nanoseconds} to Date */
function toDateSafe(v: unknown): Date | null {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'object' && 'toDate' in v && typeof (v as any).toDate === 'function') {
        return (v as any).toDate();
    }
    if (typeof v === 'object' && 'seconds' in v && typeof (v as any).seconds === 'number') {
        return new Date((v as any).seconds * 1000);
    }
    return null;
}

/** Remove undefined values recursively (Firestore rejects them). Preserves Firestore Timestamps. */
function removeUndefined(obj: unknown): unknown {
    if (obj === undefined) return undefined;
    if (obj === null) return null;
    // Preserve Firestore Timestamp (has toDate); recursing would turn it into plain {seconds, nanoseconds}
    if (obj && typeof obj === 'object' && 'toDate' in obj && typeof (obj as any).toDate === 'function') {
        return obj;
    }
    if (Array.isArray(obj)) return obj.map(removeUndefined).filter((v) => v !== undefined);
    if (typeof obj === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj)) {
            const cleaned = removeUndefined(v);
            if (cleaned !== undefined) out[k] = cleaned;
        }
        return out;
    }
    return obj;
}

function normalizeDimensions(dim: string): string {
    return dim.replace(/\s+/g, '').replace(/[×X]/g, 'x').replace(/["']/g, '').toLowerCase();
}

/** Convert a SupplierPrice (with history Dates) into a Firestore-safe object. */
export function serializeSupplierPriceForFirestore(sp: SupplierPrice): Record<string, unknown> {
    const out: Record<string, unknown> = {
        supplier: sp.supplier || '',
        piecePrice: sp.piecePrice ?? 0,
        costPerBdFt: sp.costPerBdFt ?? 0,
    };
    if (sp.lastQuoteDate instanceof Date) {
        out.lastQuoteDate = Timestamp.fromDate(sp.lastQuoteDate);
    } else if (sp.lastQuoteDate) {
        out.lastQuoteDate = sp.lastQuoteDate;
    }
    if (typeof sp.notes === 'string' && sp.notes.trim()) out.notes = sp.notes.trim();
    if (sp.priceHistory && sp.priceHistory.length > 0) {
        out.priceHistory = sp.priceHistory.map((p) => {
            const point: Record<string, unknown> = {
                piecePrice: p.piecePrice,
                costPerBdFt: p.costPerBdFt,
                recordedAt:
                    p.recordedAt instanceof Date
                        ? Timestamp.fromDate(p.recordedAt)
                        : Timestamp.fromDate(toHistoryDate(p.recordedAt)),
            };
            if (typeof p.source === 'string' && p.source.trim()) point.source = p.source.trim();
            return point;
        });
    }
    return out;
}

function parseSupplierPriceFromFirestore(sp: any): SupplierPrice {
    const history = normalizePriceHistory(sp?.priceHistory).map((p) => ({
        piecePrice: p.piecePrice,
        costPerBdFt: p.costPerBdFt,
        recordedAt: p.recordedAt,
        ...(p.source ? { source: p.source } : {}),
    }));
    const entry: SupplierPrice = {
        supplier: sp?.supplier || '',
        piecePrice: sp?.piecePrice ?? 0,
        costPerBdFt: sp?.costPerBdFt ?? 0,
    };
    const lqd = toDateSafe(sp?.lastQuoteDate);
    if (lqd) entry.lastQuoteDate = lqd;
    if (typeof sp?.notes === 'string' && sp.notes.trim()) entry.notes = sp.notes.trim();
    if (history.length > 0) entry.priceHistory = history;
    return entry;
}

export function mergeSupplierPriceEntry(
    existing: SupplierPrice | undefined,
    next: { supplier: string; piecePrice: number; costPerBdFt: number; notes?: string },
    source: 'manual' | 'import',
): SupplierPrice {
    const merged = applySupplierPriceWithHistory(existing, next, { source });
    return {
        supplier: merged.supplier,
        piecePrice: merged.piecePrice,
        costPerBdFt: merged.costPerBdFt,
        ...(merged.lastQuoteDate
            ? { lastQuoteDate: toHistoryDate(merged.lastQuoteDate) }
            : {}),
        ...(merged.notes ? { notes: merged.notes } : {}),
        ...(merged.priceHistory && merged.priceHistory.length
            ? {
                priceHistory: normalizePriceHistory(merged.priceHistory).map((p) => ({
                    piecePrice: p.piecePrice,
                    costPerBdFt: p.costPerBdFt,
                    recordedAt: p.recordedAt,
                    ...(p.source ? { source: p.source } : {}),
                })),
            }
            : {}),
    };
}

/**
 * Normalize moisture for matching: legacy/empty → unspecified; distinct green vs kiln_dried.
 */
export function normalizeMoistureContent(raw?: string | null): 'green' | 'kiln_dried' | 'unspecified' {
    const s = (raw || '').toLowerCase().trim();
    if (s === 'green' || s === 'grn') return 'green';
    if (s === 'kiln_dried' || s === 'kiln' || s === 'kd') return 'kiln_dried';
    return 'unspecified';
}

export function normalizeDimensionsForMatch(dim: string): string {
    return normalizeDimensions(dim);
}

/**
 * Find a materials catalog row by species/grade/dims/moisture.
 * Grade compare is case-insensitive via buildTimberMatchKey (#2/BTR ≡ #2/Btr).
 */
export function findTimberCatalogMatch<T extends {
    species: string;
    grade: string;
    dimensions: string;
    moistureContent?: string | null;
    piecePrice?: number;
}>(
    database: T[],
    species: string,
    grade: string,
    dimensions: string,
    moistureRaw?: string | null,
): T | undefined {
    const wantExact = buildTimberMatchKey(species, grade, dimensions, moistureRaw);
    return database.find((it) =>
        buildTimberMatchKey(it.species, it.grade, it.dimensions, it.moistureContent) === wantExact,
    );
}

/**
 * Read moisture from a Firestore timber doc (canonical field + legacy aliases).
 * Used anywhere we build a match key or merge rows so GRN/KD are not collapsed into "unspecified".
 */
export function getTimberMoistureRawFromFirestore(data: Record<string, unknown>): string | undefined {
    const v = data.moistureContent ?? (data as { moisture?: unknown }).moisture ?? (data as { dryness?: unknown }).dryness;
    if (v === null || v === undefined) return undefined;
    const s = String(v).trim();
    return s === '' ? undefined : s;
}

/**
 * Persisted moisture value for the last segment of buildTimberMatchKey (normalized id).
 */
export function canonicalMoistureContentForMatchKey(matchKey: string): TimberMoistureContent {
    const parts = matchKey.split('|');
    const m = parts[parts.length - 1] ?? 'unspecified';
    if (m === 'green') return 'green';
    if (m === 'kiln_dried') return 'kiln_dried';
    return '';
}

export type CanonicalTimberGradeOpts = {
    /**
     * When true (default), bare `#1` / `#2` collapse to `#1/BTR` / `#2/BTR`.
     * Trailing tokens (e.g. `#1/BTR FOHC`) stay distinct. Set false for
     * casing-only catalog dedupe before an explicit BTR sibling merge.
     */
    aliasBareToBtr?: boolean;
};

/**
 * Canonical grade for matchKey / new timber rows.
 * `#2/BTR`, `#2/Btr`, `#2/btr`, `#2 / BTR` → `#2/BTR` (BTR all caps).
 * Same pattern for `#1/BTR`. Bare `#1`/`#2` alias to `/BTR` unless disabled.
 * Other grades stay lowercased.
 */
export function canonicalTimberGrade(
    raw?: string | null,
    opts?: CanonicalTimberGradeOpts,
): string {
    const s = String(raw || '').trim();
    if (!s) return '';
    const btr = s.match(/^#?\s*(\d+)\s*\/\s*btr\b(.*)$/i);
    if (btr) {
        const rest = (btr[2] || '')
            .replace(/\bfohc\b/gi, 'FOHC')
            .replace(/\s+/g, ' ')
            .trim();
        return rest ? `#${btr[1]}/BTR ${rest}` : `#${btr[1]}/BTR`;
    }
    const aliasBare = opts?.aliasBareToBtr !== false;
    if (aliasBare) {
        const bare = s.match(/^#?\s*([12])\s*$/i);
        if (bare) return `#${bare[1]}/BTR`;
    }
    return s.toLowerCase();
}

/**
 * Composite key for species + grade + dimensions + moisture (used for merge, dedupe, proposal pricing).
 */
export function buildTimberMatchKey(
    species: string,
    grade: string,
    dimensions: string,
    moistureRaw?: string | null,
    opts?: CanonicalTimberGradeOpts,
): string {
    const m = normalizeMoistureContent(moistureRaw);
    return [
        (species || '').toLowerCase().trim(),
        canonicalTimberGrade(grade, opts),
        normalizeDimensions(dimensions || ''),
        m,
    ].join('|');
}

// ==========================================
// CRUD OPERATIONS
// ==========================================

/**
 * Add a single timber item
 */
export async function addTimberItem(item: TimberItemInput): Promise<string> {
    const docRef = await addDoc(collection(db, TIMBER_COLLECTION), {
        ...item,
        grade: canonicalizeGrade(canonicalTimberGrade(item.grade)) || canonicalizeGrade(item.grade) || item.grade,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    return docRef.id;
}

/**
 * Add or merge a timber item. If an item with the same species+grade+dimensions+moisture exists,
 * adds/updates the supplier in supplierPricing instead of creating a duplicate.
 */
export async function addOrMergeTimberItem(item: TimberItemInput): Promise<string> {
    const q = query(collection(db, TIMBER_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);
    const grade =
        canonicalizeGrade(canonicalTimberGrade(item.grade)) ||
        canonicalizeGrade(item.grade) ||
        item.grade;
    const key = buildTimberMatchKey(item.species, grade, item.dimensions, item.moistureContent);

    const match = snapshot.docs.find((d) => {
        const data = d.data();
        return buildTimberMatchKey(
            data.species,
            data.grade,
            data.dimensions,
            getTimberMoistureRawFromFirestore(data),
        ) === key;
    });

    if (match) {
        const data = match.data();
        const existingPricing: SupplierPrice[] = (data.supplierPricing || []).map(parseSupplierPriceFromFirestore);
        const supplierKey = (item.supplier || '').toLowerCase().trim();
        const existingIdx = existingPricing.findIndex(
            (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey
        );
        let updatedPricing: SupplierPrice[];
        if (existingIdx >= 0) {
            updatedPricing = [...existingPricing];
            updatedPricing[existingIdx] = mergeSupplierPriceEntry(
                updatedPricing[existingIdx],
                {
                    supplier: item.supplier || updatedPricing[existingIdx].supplier,
                    piecePrice: item.piecePrice,
                    costPerBdFt: item.costPerBdFt,
                },
                'import',
            );
        } else {
            updatedPricing = [
                ...existingPricing,
                mergeSupplierPriceEntry(undefined, {
                    supplier: item.supplier || '',
                    piecePrice: item.piecePrice,
                    costPerBdFt: item.costPerBdFt,
                }, 'import'),
            ];
        }
        const updates: Record<string, unknown> = {
            supplierPricing: updatedPricing.map(serializeSupplierPriceForFirestore),
            grade,
            updatedAt: Timestamp.now(),
        };
        const activePiecePrice = data.piecePrice || 0;
        if (
            !data.supplier ||
            (item.supplier && data.supplier.toLowerCase() === supplierKey) ||
            activePiecePrice <= 0
        ) {
            updates.supplier = item.supplier || data.supplier;
            updates.piecePrice = item.piecePrice;
            updates.costPerBdFt = item.costPerBdFt;
        }
        await updateDoc(doc(db, TIMBER_COLLECTION, match.id), removeUndefined(updates) as any);
        return match.id;
    }

    const initialPricing = item.supplier
        ? [mergeSupplierPriceEntry(undefined, {
            supplier: item.supplier,
            piecePrice: item.piecePrice,
            costPerBdFt: item.costPerBdFt,
        }, 'import')]
        : [];
    const docRef = await addDoc(collection(db, TIMBER_COLLECTION), {
        ...item,
        grade,
        supplierPricing: initialPricing.map(serializeSupplierPriceForFirestore),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    return docRef.id;
}

/**
 * Add multiple timber items. For each item, if species+grade+dimensions+moisture already exists,
 * merges into that item's supplierPricing; otherwise creates a new document.
 */
export async function importTimberItems(items: TimberItemInput[]): Promise<void> {
    if (items.length === 0) return;

    const q = query(collection(db, TIMBER_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);
    const keyToDoc = new Map<string, { id: string; data: any }>();
    snapshot.docs.forEach((d) => {
        const data = d.data();
        const key = buildTimberMatchKey(data.species, data.grade, data.dimensions, getTimberMoistureRawFromFirestore(data));
        keyToDoc.set(key, { id: d.id, data });
    });

    const toUpdateMap = new Map<string, Record<string, unknown>>();
    const pendingNew = new Map<string, { base: TimberItemInput; pricing: SupplierPrice[] }>();

    for (const item of items) {
        const grade =
            canonicalizeGrade(canonicalTimberGrade(item.grade)) ||
            canonicalizeGrade(item.grade) ||
            item.grade;
        const keyedItem = { ...item, grade };
        const key = buildTimberMatchKey(keyedItem.species, keyedItem.grade, keyedItem.dimensions, keyedItem.moistureContent);
        const existing = keyToDoc.get(key);

        if (existing) {
            const data = existing.data;
            const existingPricing: SupplierPrice[] = (data.supplierPricing || []).map(parseSupplierPriceFromFirestore);
            const supplierKey = (item.supplier || '').toLowerCase().trim();
            const existingIdx = existingPricing.findIndex(
                (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey
            );
            let updatedPricing: SupplierPrice[];
            if (existingIdx >= 0) {
                updatedPricing = [...existingPricing];
                updatedPricing[existingIdx] = mergeSupplierPriceEntry(
                    updatedPricing[existingIdx],
                    {
                        supplier: item.supplier || updatedPricing[existingIdx].supplier,
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    },
                    'import',
                );
            } else {
                updatedPricing = [
                    ...existingPricing,
                    mergeSupplierPriceEntry(undefined, {
                        supplier: item.supplier || '',
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    }, 'import'),
                ];
            }
            const updates: Record<string, unknown> = {
                supplierPricing: updatedPricing.map(serializeSupplierPriceForFirestore),
                grade,
                updatedAt: Timestamp.now(),
            };
            if (!data.supplier || (item.supplier && data.supplier.toLowerCase() === supplierKey)) {
                updates.supplier = item.supplier || data.supplier;
                updates.piecePrice = item.piecePrice;
                updates.costPerBdFt = item.costPerBdFt;
            }
            toUpdateMap.set(existing.id, updates);
            keyToDoc.set(key, { id: existing.id, data: { ...data, ...updates, supplierPricing: updatedPricing } });
        } else {
            const entry = pendingNew.get(key);
            const spEntry = mergeSupplierPriceEntry(undefined, {
                supplier: item.supplier || '',
                piecePrice: item.piecePrice,
                costPerBdFt: item.costPerBdFt,
            }, 'import');
            if (entry) {
                const supplierKey = (item.supplier || '').toLowerCase().trim();
                const idx = entry.pricing.findIndex(
                    (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey
                );
                if (idx >= 0) {
                    entry.pricing[idx] = mergeSupplierPriceEntry(entry.pricing[idx], {
                        supplier: item.supplier || entry.pricing[idx].supplier,
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    }, 'import');
                } else {
                    entry.pricing.push(spEntry);
                }
            } else {
                pendingNew.set(key, {
                    base: { ...keyedItem },
                    pricing: item.supplier || item.piecePrice > 0 ? [spEntry] : [],
                });
            }
        }
    }

    for (const [id, updates] of Array.from(toUpdateMap.entries())) {
        await updateDoc(doc(db, TIMBER_COLLECTION, id), removeUndefined(updates) as any);
    }

    const toAdd = Array.from(pendingNew.values());
    if (toAdd.length > 0) {
        const batch = writeBatch(db);
        for (const { base, pricing } of toAdd) {
            const best = pricing.length > 0
                ? pricing.reduce((a, b) => (a.piecePrice || 0) >= (b.piecePrice || 0) ? a : b, pricing[0])
                : null;
            const docRef = doc(collection(db, TIMBER_COLLECTION));
            batch.set(docRef, {
                ...base,
                supplierPricing: pricing.map(serializeSupplierPriceForFirestore),
                supplier: best?.supplier || base.supplier || '',
                piecePrice: best?.piecePrice ?? base.piecePrice ?? 0,
                costPerBdFt: best?.costPerBdFt ?? base.costPerBdFt ?? 0,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
        }
        await batch.commit();
    }

}

/**
 * Update a single timber item
 */
export async function updateTimberItem(
    id: string,
    updates: Partial<TimberItemInput>
): Promise<void> {
    const docRef = doc(db, TIMBER_COLLECTION, id);
    const payload: Record<string, unknown> = { ...updates, updatedAt: Timestamp.now() };
    if (updates.grade != null) {
        payload.grade = canonicalizeGrade(updates.grade) || updates.grade;
    }
    if (Array.isArray(updates.supplierPricing)) {
        payload.supplierPricing = updates.supplierPricing.map(serializeSupplierPriceForFirestore);
    }
    const cleaned = removeUndefined(payload);
    await updateDoc(docRef, cleaned as any);
}

/**
 * Bulk update multiple timber items
 */
export async function bulkUpdateTimberItems(
    ids: string[],
    updates: Partial<TimberItemInput>
): Promise<void> {
    const batch = writeBatch(db);

    const cleaned = removeUndefined({ ...updates, updatedAt: Timestamp.now() });
    ids.forEach((id) => {
        const docRef = doc(db, TIMBER_COLLECTION, id);
        batch.update(docRef, cleaned as any);
    });

    await batch.commit();
}

/**
 * Delete a timber item
 */
export async function deleteTimberItem(id: string): Promise<void> {
    const docRef = doc(db, TIMBER_COLLECTION, id);
    await deleteDoc(docRef);
}

/**
 * Bulk delete multiple timber items
 */
export async function bulkDeleteTimberItems(ids: string[]): Promise<void> {
    const batch = writeBatch(db);

    ids.forEach((id) => {
        const docRef = doc(db, TIMBER_COLLECTION, id);
        batch.delete(docRef);
    });

    await batch.commit();
}

// ==========================================
// REAL-TIME SUBSCRIPTION
// ==========================================

/**
 * Subscribe to real-time updates for timber items
 */
export function subscribeToTimberItems(
    callback: (items: TimberItem[]) => void,
    onError?: (error: Error) => void
): Unsubscribe {
    // Simple query - only order by species to avoid composite index requirement
    const q = query(
        collection(db, TIMBER_COLLECTION),
        orderBy('species')
    );

    return onSnapshot(
        q,
        (snapshot) => {
            const items: TimberItem[] = snapshot.docs.map((doc) => {
                const data = doc.data();
                // Parse supplierPricing array if present
                const supplierPricing: SupplierPrice[] = (data.supplierPricing || []).map(parseSupplierPriceFromFirestore);
                const rawM = getTimberMoistureRawFromFirestore(data);
                const n = normalizeMoistureContent(rawM);
                const moistureContent: TimberMoistureContent | undefined =
                    n === 'green' ? 'green' : n === 'kiln_dried' ? 'kiln_dried' : undefined;
                return {
                    id: doc.id,
                    species: data.species || '',
                    grade: data.grade || '',
                    dimensions: data.dimensions || '',
                    moistureContent,
                    piecePrice: data.piecePrice || 0,
                    costPerBdFt: data.costPerBdFt || 0,
                    boardFeet: data.boardFeet || 0,
                    supplier: data.supplier || '',
                    supplierPricing: supplierPricing.length > 0 ? supplierPricing : undefined,
                    createdAt: toDateSafe(data.createdAt) || new Date(),
                    updatedAt: toDateSafe(data.updatedAt) || new Date(),
                };
            });
            callback(items);
        },
        (error) => {
            console.error('[MaterialsDB] Subscription error:', error);
            if (onError) onError(error);
        }
    );
}

// ==========================================
// MIGRATION
// ==========================================

/**
 * One-time migration: merge flat rows (each with a supplier tag)
 * into single documents with supplierPricing[] arrays.
 * Groups by species + grade + dimensions + moisture (same key as dedupe / pricing).
 */
export async function migrateTimberToSupplierPricing(): Promise<{ merged: number; deleted: number }> {
    const q = query(collection(db, TIMBER_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);

    // Group by species + grade + dimensions + moisture (legacy moisture/dryness coalesced)
    const groups = new Map<string, { docs: { id: string; data: any }[] }>();

    snapshot.docs.forEach((d) => {
        const data = d.data();
        const key = buildTimberMatchKey(data.species, data.grade, data.dimensions, getTimberMoistureRawFromFirestore(data));

        if (!groups.has(key)) {
            groups.set(key, { docs: [] });
        }
        groups.get(key)!.docs.push({ id: d.id, data });
    });

    let merged = 0;
    let deleted = 0;

    for (const [groupKey, group] of Array.from(groups)) {
        if (group.docs.length <= 1) {
            // Single row — add supplierPricing if it has a supplier but no array yet
            const { id, data } = group.docs[0];
            if (data.supplier && !data.supplierPricing) {
                const supplierPricing = [{
                    supplier: data.supplier,
                    piecePrice: data.piecePrice || 0,
                    costPerBdFt: data.costPerBdFt || 0,
                    lastQuoteDate: Timestamp.now(),
                }];
                await updateDoc(doc(db, TIMBER_COLLECTION, id), removeUndefined({
                    supplierPricing,
                    moistureContent: canonicalMoistureContentForMatchKey(groupKey),
                    updatedAt: Timestamp.now(),
                }) as any);
                merged++;
            }
            continue;
        }

        // Multiple rows — merge into the first doc, delete the rest
        const primaryDoc = group.docs[0];
        const supplierPricing: any[] = [];

        for (const { data } of group.docs) {
            if (data.supplier) {
                supplierPricing.push({
                    supplier: data.supplier,
                    piecePrice: data.piecePrice || 0,
                    costPerBdFt: data.costPerBdFt || 0,
                    lastQuoteDate: Timestamp.now(),
                });
            }
        }

        // Update primary doc with merged supplierPricing
        await updateDoc(doc(db, TIMBER_COLLECTION, primaryDoc.id), removeUndefined({
            supplierPricing,
            moistureContent: canonicalMoistureContentForMatchKey(groupKey),
            updatedAt: Timestamp.now(),
        }) as any);
        merged++;

        // Delete duplicate docs
        const batch = writeBatch(db);
        for (let i = 1; i < group.docs.length; i++) {
            batch.delete(doc(db, TIMBER_COLLECTION, group.docs[i].id));
            deleted++;
        }
        await batch.commit();
    }

    return { merged, deleted };
}

const ESTIMATE_SUPPLIER = 'estimate';

function supplierIsEstimate(name?: string | null): boolean {
    return (name || '').trim().toLowerCase() === ESTIMATE_SUPPLIER;
}

function supplierQuoteTime(sp: any): number {
    const d = sp?.lastQuoteDate;
    if (!d) return 0;
    if (typeof d?.toDate === 'function') {
        try {
            return d.toDate().getTime() || 0;
        } catch {
            return 0;
        }
    }
    if (d instanceof Date) return d.getTime() || 0;
    if (typeof d?.seconds === 'number') return d.seconds * 1000;
    return 0;
}

/** Prefer non-Estimate, then richer supplierPricing, then higher piecePrice. */
function pickCatalogPrimaryDoc(rows: { id: string; data: any }[]): { id: string; data: any } {
    return rows.reduce((best, cur) => {
        const bestEst = supplierIsEstimate(best.data.supplier);
        const curEst = supplierIsEstimate(cur.data.supplier);
        if (bestEst !== curEst) return curEst ? best : cur;
        const bl = Array.isArray(best.data.supplierPricing) ? best.data.supplierPricing.length : 0;
        const cl = Array.isArray(cur.data.supplierPricing) ? cur.data.supplierPricing.length : 0;
        if (cl !== bl) return cl > bl ? cur : best;
        const bp = Number(best.data.piecePrice) || 0;
        const cp = Number(cur.data.piecePrice) || 0;
        return cp > bp ? cur : best;
    }, rows[0]!);
}

/** Same supplier: newer lastQuoteDate wins; else higher piecePrice. */
function pickBetterSupplierEntry(a: any, b: any): any {
    const ta = supplierQuoteTime(a);
    const tb = supplierQuoteTime(b);
    if (ta !== tb) return tb > ta ? b : a;
    return (Number(b.piecePrice) || 0) >= (Number(a.piecePrice) || 0) ? b : a;
}

function pickActiveSupplierFromPricing(pricing: any[], preferred?: string | null): any | undefined {
    if (!pricing.length) return undefined;
    const pref = (preferred || '').toLowerCase().trim();
    if (pref && !supplierIsEstimate(preferred)) {
        const hit = pricing.find((sp) => (sp.supplier || '').toLowerCase().trim() === pref);
        if (hit) return hit;
    }
    const nonEstimate = pricing.filter((sp) => !supplierIsEstimate(sp.supplier));
    const pool = nonEstimate.length ? nonEstimate : pricing;
    return pool.reduce((best, cur) => pickBetterSupplierEntry(best, cur), pool[0]);
}

/**
 * Deduplication: for each timber item, remove duplicate supplier entries
 * from the supplierPricing[] array. If same supplier appears multiple times,
 * keep the newer quote (else higher piecePrice).
 * Also merges separate documents that share the same match key (species + grade
 * with #1/#2 ≡ /BTR + dimensions + moisture), including casing drift.
 */
export async function deduplicateSupplierPricing(): Promise<{
    itemsCleaned: number;
    docsMerged: number;
    gradesNormalized: number;
}> {
    const q = query(collection(db, TIMBER_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);

    let itemsCleaned = 0;
    let docsMerged = 0;
    let gradesNormalized = 0;

    const groups = new Map<string, { id: string; data: any }[]>();
    snapshot.docs.forEach((d) => {
        const data = d.data();
        const key = buildTimberMatchKey(
            data.species,
            data.grade,
            data.dimensions,
            getTimberMoistureRawFromFirestore(data),
        );
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push({ id: d.id, data });
    });

    for (const [matchKey, docs] of Array.from(groups)) {
        const primary = pickCatalogPrimaryDoc(docs);
        const rawGrade = canonicalTimberGrade(
            primary.data.grade || docs.map((d) => d.data.grade).find(Boolean) || '',
        );
        const canonicalGrade = canonicalizeGrade(rawGrade) || rawGrade;
        const canonicalMoisture = canonicalMoistureContentForMatchKey(matchKey);

        if (docs.length > 1) {
            const allPricing: any[] = [];
            for (const { data } of docs) {
                if (data.supplierPricing && Array.isArray(data.supplierPricing)) {
                    allPricing.push(...data.supplierPricing);
                }
                if (data.supplier && (!data.supplierPricing || data.supplierPricing.length === 0)) {
                    allPricing.push({
                        supplier: data.supplier,
                        piecePrice: data.piecePrice ?? 0,
                        costPerBdFt: data.costPerBdFt ?? 0,
                    });
                }
            }

            const bestBySupplier = new Map<string, any>();
            for (const sp of allPricing) {
                const supKey = (sp.supplier || '').toLowerCase().trim();
                const existing = bestBySupplier.get(supKey);
                if (!existing) bestBySupplier.set(supKey, sp);
                else bestBySupplier.set(supKey, pickBetterSupplierEntry(existing, sp));
            }
            const dedupedPricing = Array.from(bestBySupplier.values());
            const best = pickActiveSupplierFromPricing(dedupedPricing, primary.data.supplier);

            const gradeChanged = String(primary.data.grade || '') !== canonicalGrade;
            await updateDoc(doc(db, TIMBER_COLLECTION, primary.id), removeUndefined({
                supplierPricing: dedupedPricing,
                supplier: best?.supplier || primary.data.supplier || '',
                piecePrice: best?.piecePrice ?? primary.data.piecePrice ?? 0,
                costPerBdFt: best?.costPerBdFt ?? primary.data.costPerBdFt ?? 0,
                grade: canonicalGrade || primary.data.grade || '',
                moistureContent: canonicalMoisture,
                updatedAt: Timestamp.now(),
            }) as any);
            if (gradeChanged) gradesNormalized += 1;

            const batch = writeBatch(db);
            for (const row of docs) {
                if (row.id === primary.id) continue;
                batch.delete(doc(db, TIMBER_COLLECTION, row.id));
                docsMerged++;
            }
            await batch.commit();
            itemsCleaned++;
        } else {
            const { id, data } = docs[0]!;
            const pricing = data.supplierPricing;
            const targetGrade =
                canonicalizeGrade(canonicalTimberGrade(data.grade)) || data.grade;
            const gradeChanged = String(data.grade || '') !== String(targetGrade || '');

            let dedupedPricing = pricing;
            let pricingChanged = false;
            if (pricing && Array.isArray(pricing) && pricing.length > 1) {
                const bestBySupplier = new Map<string, any>();
                for (const sp of pricing) {
                    const key = (sp.supplier || '').toLowerCase().trim();
                    const existing = bestBySupplier.get(key);
                    if (!existing) bestBySupplier.set(key, sp);
                    else bestBySupplier.set(key, pickBetterSupplierEntry(existing, sp));
                }
                if (bestBySupplier.size < pricing.length) {
                    dedupedPricing = Array.from(bestBySupplier.values());
                    pricingChanged = true;
                }
            }

            if (pricingChanged || gradeChanged) {
                const active = pricingChanged
                    ? pickActiveSupplierFromPricing(dedupedPricing || [], data.supplier)
                    : undefined;
                await updateDoc(doc(db, TIMBER_COLLECTION, id), removeUndefined({
                    ...(pricingChanged ? { supplierPricing: dedupedPricing } : {}),
                    ...(gradeChanged ? { grade: targetGrade } : {}),
                    ...(active && !supplierIsEstimate(active.supplier)
                        ? {
                            supplier: active.supplier,
                            piecePrice: active.piecePrice ?? data.piecePrice ?? 0,
                            costPerBdFt: active.costPerBdFt ?? data.costPerBdFt ?? 0,
                        }
                        : {}),
                    moistureContent: canonicalMoisture,
                    updatedAt: Timestamp.now(),
                }) as any);
                if (pricingChanged) itemsCleaned++;
                if (gradeChanged) gradesNormalized++;
            }
        }
    }

    return { itemsCleaned, docsMerged, gradesNormalized };
}

// ==========================================
// GLULAM ITEMS (timber-shaped pricing, no moisture)
// ==========================================

export interface GlulamItem {
    id: string;
    species: string;
    grade: string;
    dimensions: string;
    piecePrice: number;
    costPerBdFt: number;
    boardFeet: number;
    supplier?: string;
    supplierPricing?: SupplierPrice[];
    createdAt: Date;
    updatedAt: Date;
}

export interface GlulamItemInput {
    species: string;
    grade: string;
    dimensions: string;
    piecePrice: number;
    costPerBdFt: number;
    boardFeet: number;
    supplier?: string;
    supplierPricing?: SupplierPrice[];
}

const GLULAM_COLLECTION = 'materialsDatabase_glulam';

/** Canonical glulam grade for match keys (24F-V4, 20F-EWS, etc.). */
export function canonicalGlulamGrade(raw?: string | null): string {
    const s = String(raw || '').trim();
    if (!s) return '';
    return s.replace(/\s+/g, ' ').toUpperCase();
}

export function buildGlulamMatchKey(species: string, grade: string, dimensions: string): string {
    return [
        (species || '').toLowerCase().trim(),
        canonicalGlulamGrade(grade),
        normalizeDimensions(dimensions || ''),
    ].join('|');
}

export function findGlulamCatalogMatch<T extends {
    species: string;
    grade: string;
    dimensions: string;
}>(
    database: T[],
    species: string,
    grade: string,
    dimensions: string,
): T | undefined {
    const want = buildGlulamMatchKey(species, grade, dimensions);
    return database.find((it) => buildGlulamMatchKey(it.species, it.grade, it.dimensions) === want);
}

function parseGlulamItemFromDoc(id: string, data: Record<string, unknown>): GlulamItem {
    const supplierPricing: SupplierPrice[] = (Array.isArray(data.supplierPricing)
        ? data.supplierPricing
        : []
    ).map(parseSupplierPriceFromFirestore);
    return {
        id,
        species: String(data.species || ''),
        grade: String(data.grade || ''),
        dimensions: String(data.dimensions || ''),
        piecePrice: Number(data.piecePrice) || 0,
        costPerBdFt: Number(data.costPerBdFt) || 0,
        boardFeet: Number(data.boardFeet) || 0,
        supplier: typeof data.supplier === 'string' ? data.supplier : '',
        supplierPricing: supplierPricing.length > 0 ? supplierPricing : undefined,
        createdAt: toDateSafe(data.createdAt) || new Date(),
        updatedAt: toDateSafe(data.updatedAt) || new Date(),
    };
}

export async function addGlulamItem(item: GlulamItemInput): Promise<string> {
    const grade = canonicalGlulamGrade(item.grade) || item.grade;
    const docRef = await addDoc(collection(db, GLULAM_COLLECTION), {
        ...item,
        grade,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    return docRef.id;
}

export async function addOrMergeGlulamItem(item: GlulamItemInput): Promise<string> {
    const q = query(collection(db, GLULAM_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);
    const grade = canonicalGlulamGrade(item.grade) || item.grade;
    const key = buildGlulamMatchKey(item.species, grade, item.dimensions);

    const match = snapshot.docs.find((d) => {
        const data = d.data();
        return buildGlulamMatchKey(data.species, data.grade, data.dimensions) === key;
    });

    if (match) {
        const data = match.data();
        const existingPricing: SupplierPrice[] = (data.supplierPricing || []).map(parseSupplierPriceFromFirestore);
        const supplierKey = (item.supplier || '').toLowerCase().trim();
        const existingIdx = existingPricing.findIndex(
            (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey,
        );
        let updatedPricing: SupplierPrice[];
        if (existingIdx >= 0) {
            updatedPricing = [...existingPricing];
            updatedPricing[existingIdx] = mergeSupplierPriceEntry(
                updatedPricing[existingIdx],
                {
                    supplier: item.supplier || updatedPricing[existingIdx].supplier,
                    piecePrice: item.piecePrice,
                    costPerBdFt: item.costPerBdFt,
                },
                'import',
            );
        } else {
            updatedPricing = [
                ...existingPricing,
                mergeSupplierPriceEntry(undefined, {
                    supplier: item.supplier || '',
                    piecePrice: item.piecePrice,
                    costPerBdFt: item.costPerBdFt,
                }, 'import'),
            ];
        }
        const updates: Record<string, unknown> = {
            supplierPricing: updatedPricing.map(serializeSupplierPriceForFirestore),
            grade,
            updatedAt: Timestamp.now(),
        };
        const activePiecePrice = data.piecePrice || 0;
        if (
            !data.supplier ||
            (item.supplier && data.supplier.toLowerCase() === supplierKey) ||
            activePiecePrice <= 0
        ) {
            updates.supplier = item.supplier || data.supplier;
            updates.piecePrice = item.piecePrice;
            updates.costPerBdFt = item.costPerBdFt;
        }
        await updateDoc(doc(db, GLULAM_COLLECTION, match.id), removeUndefined(updates) as any);
        return match.id;
    }

    const initialPricing = item.supplier
        ? [mergeSupplierPriceEntry(undefined, {
            supplier: item.supplier,
            piecePrice: item.piecePrice,
            costPerBdFt: item.costPerBdFt,
        }, 'import')]
        : [];
    const docRef = await addDoc(collection(db, GLULAM_COLLECTION), {
        ...item,
        grade,
        supplierPricing: initialPricing.map(serializeSupplierPriceForFirestore),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    return docRef.id;
}

export async function importGlulamItems(items: GlulamItemInput[]): Promise<void> {
    if (items.length === 0) return;

    const q = query(collection(db, GLULAM_COLLECTION), orderBy('species'));
    const snapshot = await getDocs(q);
    const keyToDoc = new Map<string, { id: string; data: any }>();
    snapshot.docs.forEach((d) => {
        const data = d.data();
        const key = buildGlulamMatchKey(data.species, data.grade, data.dimensions);
        keyToDoc.set(key, { id: d.id, data });
    });

    const toUpdateMap = new Map<string, Record<string, unknown>>();
    const pendingNew = new Map<string, { base: GlulamItemInput; pricing: SupplierPrice[] }>();

    for (const item of items) {
        const grade = canonicalGlulamGrade(item.grade) || item.grade;
        const keyedItem = { ...item, grade };
        const key = buildGlulamMatchKey(keyedItem.species, keyedItem.grade, keyedItem.dimensions);
        const existing = keyToDoc.get(key);

        if (existing) {
            const data = existing.data;
            const existingPricing: SupplierPrice[] = (data.supplierPricing || []).map(parseSupplierPriceFromFirestore);
            const supplierKey = (item.supplier || '').toLowerCase().trim();
            const existingIdx = existingPricing.findIndex(
                (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey,
            );
            let updatedPricing: SupplierPrice[];
            if (existingIdx >= 0) {
                updatedPricing = [...existingPricing];
                updatedPricing[existingIdx] = mergeSupplierPriceEntry(
                    updatedPricing[existingIdx],
                    {
                        supplier: item.supplier || updatedPricing[existingIdx].supplier,
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    },
                    'import',
                );
            } else {
                updatedPricing = [
                    ...existingPricing,
                    mergeSupplierPriceEntry(undefined, {
                        supplier: item.supplier || '',
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    }, 'import'),
                ];
            }
            const updates: Record<string, unknown> = {
                supplierPricing: updatedPricing.map(serializeSupplierPriceForFirestore),
                grade,
                updatedAt: Timestamp.now(),
            };
            if (!data.supplier || (item.supplier && data.supplier.toLowerCase() === supplierKey)) {
                updates.supplier = item.supplier || data.supplier;
                updates.piecePrice = item.piecePrice;
                updates.costPerBdFt = item.costPerBdFt;
            }
            toUpdateMap.set(existing.id, updates);
            keyToDoc.set(key, { id: existing.id, data: { ...data, ...updates, supplierPricing: updatedPricing } });
        } else {
            const entry = pendingNew.get(key);
            const spEntry = mergeSupplierPriceEntry(undefined, {
                supplier: item.supplier || '',
                piecePrice: item.piecePrice,
                costPerBdFt: item.costPerBdFt,
            }, 'import');
            if (entry) {
                const supplierKey = (item.supplier || '').toLowerCase().trim();
                const idx = entry.pricing.findIndex(
                    (sp) => (sp.supplier || '').toLowerCase().trim() === supplierKey,
                );
                if (idx >= 0) {
                    entry.pricing[idx] = mergeSupplierPriceEntry(entry.pricing[idx], {
                        supplier: item.supplier || entry.pricing[idx].supplier,
                        piecePrice: item.piecePrice,
                        costPerBdFt: item.costPerBdFt,
                    }, 'import');
                } else {
                    entry.pricing.push(spEntry);
                }
            } else {
                pendingNew.set(key, {
                    base: { ...keyedItem },
                    pricing: item.supplier || item.piecePrice > 0 ? [spEntry] : [],
                });
            }
        }
    }

    for (const [id, updates] of Array.from(toUpdateMap.entries())) {
        await updateDoc(doc(db, GLULAM_COLLECTION, id), removeUndefined(updates) as any);
    }

    const toAdd = Array.from(pendingNew.values());
    if (toAdd.length > 0) {
        const batch = writeBatch(db);
        for (const { base, pricing } of toAdd) {
            const best = pricing.length > 0
                ? pricing.reduce((a, b) => (a.piecePrice || 0) >= (b.piecePrice || 0) ? a : b, pricing[0])
                : null;
            const docRef = doc(collection(db, GLULAM_COLLECTION));
            batch.set(docRef, {
                ...base,
                supplierPricing: pricing.map(serializeSupplierPriceForFirestore),
                supplier: best?.supplier || base.supplier || '',
                piecePrice: best?.piecePrice ?? base.piecePrice ?? 0,
                costPerBdFt: best?.costPerBdFt ?? base.costPerBdFt ?? 0,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
        }
        await batch.commit();
    }
}

export async function updateGlulamItem(id: string, updates: Partial<GlulamItemInput>): Promise<void> {
    const docRef = doc(db, GLULAM_COLLECTION, id);
    const payload: Record<string, unknown> = { ...updates, updatedAt: Timestamp.now() };
    if (updates.grade != null) {
        payload.grade = canonicalGlulamGrade(updates.grade) || updates.grade;
    }
    if (Array.isArray(updates.supplierPricing)) {
        payload.supplierPricing = updates.supplierPricing.map(serializeSupplierPriceForFirestore);
    }
    await updateDoc(docRef, removeUndefined(payload) as any);
}

export async function bulkUpdateGlulamItems(ids: string[], updates: Partial<GlulamItemInput>): Promise<void> {
    const batch = writeBatch(db);
    const cleaned = removeUndefined({ ...updates, updatedAt: Timestamp.now() });
    ids.forEach((id) => {
        batch.update(doc(db, GLULAM_COLLECTION, id), cleaned as any);
    });
    await batch.commit();
}

export async function deleteGlulamItem(id: string): Promise<void> {
    await deleteDoc(doc(db, GLULAM_COLLECTION, id));
}

export async function bulkDeleteGlulamItems(ids: string[]): Promise<void> {
    const batch = writeBatch(db);
    ids.forEach((id) => batch.delete(doc(db, GLULAM_COLLECTION, id)));
    await batch.commit();
}

export function subscribeToGlulamItems(
    callback: (items: GlulamItem[]) => void,
    onError?: (error: Error) => void,
): Unsubscribe {
    const q = query(collection(db, GLULAM_COLLECTION), orderBy('species'));
    return onSnapshot(
        q,
        (snapshot) => {
            callback(snapshot.docs.map((d) => parseGlulamItemFromDoc(d.id, d.data())));
        },
        (error) => {
            console.error('[MaterialsDB] Glulam subscription error:', error);
            if (onError) onError(error);
        },
    );
}

// ==========================================
// HARDWARE ITEMS
// ==========================================

export type HardwareCategory = 'Pegs' | 'Fasteners' | 'Post Bases' | 'Decking' | 'Other';

export const HARDWARE_CATEGORIES: HardwareCategory[] = ['Pegs', 'Fasteners', 'Post Bases', 'Decking', 'Other'];

export type HardwarePriceHistorySource =
  | 'manual'
  | 'import'
  | 'materials_price_approve'
  | 'seed';

export interface HardwarePricePoint {
  unitPrice: number;
  recordedAt: Date;
  source?: HardwarePriceHistorySource | string;
}

/** Per-supplier pricing for hardware/fastener SKUs (Rothoblaas, etc.). */
export interface HardwareSupplierPrice {
  supplier: string;
  unitPrice: number;
  sku?: string;
  lastQuoteDate?: Date;
  notes?: string;
  /** Oldest → newest; live unitPrice should match the last point. */
  priceHistory?: HardwarePricePoint[];
}

export interface HardwareItem {
    id: string;
    category: HardwareCategory;
    name: string;
    description: string;
    unitPrice: number;
    unit: string; // 'pack', 'each', 'box', 'sq ft', etc.
    widthInches?: number; // Board width in inches (Decking only — for sq ft → linear ft conversion)
    /** Supplier product code (Rothoblaas SKU preferred). */
    sku?: string;
    /** Active / primary supplier name. */
    supplier?: string;
    /** All supplier prices + append-only priceHistory (timber parity). */
    supplierPricing?: HardwareSupplierPrice[];
    createdAt: Date;
    updatedAt: Date;
}

export interface HardwareItemInput {
    category: HardwareCategory;
    name: string;
    description: string;
    unitPrice: number;
    unit: string;
    widthInches?: number;
    sku?: string;
    supplier?: string;
    supplierPricing?: HardwareSupplierPrice[];
}

const HARDWARE_COLLECTION = 'materialsDatabase_hardware';

export function serializeHardwareSupplierPriceForFirestore(
    sp: HardwareSupplierPrice,
): Record<string, unknown> {
    const out: Record<string, unknown> = {
        supplier: sp.supplier || '',
        unitPrice: sp.unitPrice ?? 0,
    };
    if (typeof sp.sku === 'string' && sp.sku.trim()) out.sku = sp.sku.trim();
    if (sp.lastQuoteDate instanceof Date) {
        out.lastQuoteDate = Timestamp.fromDate(sp.lastQuoteDate);
    } else if (sp.lastQuoteDate) {
        out.lastQuoteDate = sp.lastQuoteDate;
    }
    if (typeof sp.notes === 'string' && sp.notes.trim()) out.notes = sp.notes.trim();
    if (sp.priceHistory && sp.priceHistory.length > 0) {
        out.priceHistory = sp.priceHistory.map((p) => {
            const point: Record<string, unknown> = {
                unitPrice: p.unitPrice,
                recordedAt:
                    p.recordedAt instanceof Date
                        ? Timestamp.fromDate(p.recordedAt)
                        : Timestamp.fromDate(toHardwareHistoryDate(p.recordedAt)),
            };
            if (typeof p.source === 'string' && p.source.trim()) point.source = p.source.trim();
            return point;
        });
    }
    return out;
}

function parseHardwareSupplierPriceFromFirestore(sp: any): HardwareSupplierPrice {
    const history = normalizeHardwarePriceHistory(sp?.priceHistory).map((p) => ({
        unitPrice: p.unitPrice,
        recordedAt: p.recordedAt,
        ...(p.source ? { source: p.source } : {}),
    }));
    const entry: HardwareSupplierPrice = {
        supplier: sp?.supplier || '',
        unitPrice: sp?.unitPrice ?? 0,
    };
    if (typeof sp?.sku === 'string' && sp.sku.trim()) entry.sku = sp.sku.trim();
    const lqd = toDateSafe(sp?.lastQuoteDate);
    if (lqd) entry.lastQuoteDate = lqd;
    if (typeof sp?.notes === 'string' && sp.notes.trim()) entry.notes = sp.notes.trim();
    if (history.length > 0) entry.priceHistory = history;
    return entry;
}

function mergeHardwareSupplierPriceEntry(
    existing: HardwareSupplierPrice | undefined,
    next: { supplier: string; unitPrice: number; sku?: string; notes?: string },
    source: 'manual' | 'import',
): HardwareSupplierPrice {
    const merged = applyHardwareSupplierPriceWithHistory(existing, next, { source });
    return {
        supplier: merged.supplier,
        unitPrice: merged.unitPrice,
        ...(merged.sku ? { sku: merged.sku } : {}),
        ...(merged.lastQuoteDate
            ? { lastQuoteDate: toHardwareHistoryDate(merged.lastQuoteDate) }
            : {}),
        ...(merged.notes ? { notes: merged.notes } : {}),
        ...(merged.priceHistory && merged.priceHistory.length
            ? {
                priceHistory: normalizeHardwarePriceHistory(merged.priceHistory).map((p) => ({
                    unitPrice: p.unitPrice,
                    recordedAt: p.recordedAt,
                    ...(p.source ? { source: p.source } : {}),
                })),
            }
            : {}),
    };
}

function parseHardwareItemFromFirestore(id: string, data: Record<string, any>): HardwareItem {
    const supplierPricing = Array.isArray(data.supplierPricing)
        ? data.supplierPricing.map(parseHardwareSupplierPriceFromFirestore)
        : undefined;
    const supplier = typeof data.supplier === 'string' && data.supplier.trim()
        ? data.supplier.trim()
        : undefined;
    let unitPrice = typeof data.unitPrice === 'number' && Number.isFinite(data.unitPrice)
        ? data.unitPrice
        : 0;
    // Prefer active supplierPricing when top-level unitPrice was never synced.
    if ((!unitPrice || unitPrice <= 0) && supplierPricing && supplierPricing.length > 0) {
        const want = (supplier || '').trim().toLowerCase();
        const match =
            (want
                ? supplierPricing.find((sp) => (sp.supplier || '').trim().toLowerCase() === want)
                : null)
            || supplierPricing.find((sp) => typeof sp.unitPrice === 'number' && sp.unitPrice > 0)
            || supplierPricing[0];
        if (match && typeof match.unitPrice === 'number' && Number.isFinite(match.unitPrice)) {
            unitPrice = match.unitPrice;
        }
    }
    return {
        id,
        category: data.category || 'Other',
        name: data.name || '',
        description: data.description || '',
        unitPrice,
        unit: data.unit || 'each',
        widthInches: data.widthInches || undefined,
        sku: typeof data.sku === 'string' && data.sku.trim() ? data.sku.trim() : undefined,
        supplier,
        supplierPricing,
        createdAt: toDateSafe(data.createdAt) || new Date(),
        updatedAt: toDateSafe(data.updatedAt) || new Date(),
    };
}

/**
 * Add a single hardware item. When supplier is set, seeds supplierPricing + priceHistory.
 */
export async function addHardwareItem(item: HardwareItemInput): Promise<string> {
    const supplier = (item.supplier || '').trim();
    let supplierPricing = item.supplierPricing;
    if ((!supplierPricing || supplierPricing.length === 0) && supplier) {
        supplierPricing = [
            mergeHardwareSupplierPriceEntry(
                undefined,
                {
                    supplier,
                    unitPrice: item.unitPrice,
                    sku: item.sku,
                },
                'manual',
            ),
        ];
    }
    const payload = removeUndefined({
        category: item.category,
        name: item.name,
        description: item.description,
        unitPrice: item.unitPrice,
        unit: item.unit,
        widthInches: item.widthInches,
        sku: item.sku,
        supplier: supplier || undefined,
        supplierPricing: supplierPricing?.map(serializeHardwareSupplierPriceForFirestore),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    const docRef = await addDoc(collection(db, HARDWARE_COLLECTION), payload as any);
    return docRef.id;
}

/**
 * Update a single hardware item. Price/supplier changes append priceHistory
 * (same philosophy as timber manual edits).
 */
export async function updateHardwareItem(
    id: string,
    updates: Partial<HardwareItemInput>
): Promise<void> {
    const docRef = doc(db, HARDWARE_COLLECTION, id);
    const existingSnap = await getDoc(docRef);
    const existing = existingSnap.exists()
        ? parseHardwareItemFromFirestore(id, existingSnap.data() as Record<string, any>)
        : null;

    const nextUnitPrice =
        typeof updates.unitPrice === 'number' ? updates.unitPrice : existing?.unitPrice ?? 0;
    const nextSupplier = (
        typeof updates.supplier === 'string' ? updates.supplier : existing?.supplier || ''
    ).trim();
    const nextSku =
        typeof updates.sku === 'string' ? updates.sku.trim() : existing?.sku;

    let supplierPricing = updates.supplierPricing ?? existing?.supplierPricing;
    if (nextSupplier) {
        const list = [...(supplierPricing || [])];
        const idx = list.findIndex(
            (sp) => sp.supplier.trim().toLowerCase() === nextSupplier.toLowerCase(),
        );
        const merged = mergeHardwareSupplierPriceEntry(
            idx >= 0 ? list[idx] : undefined,
            { supplier: nextSupplier, unitPrice: nextUnitPrice, sku: nextSku },
            'manual',
        );
        if (idx >= 0) list[idx] = merged;
        else list.push(merged);
        supplierPricing = list;
    }

    const cleaned = removeUndefined({
        ...updates,
        sku: nextSku,
        supplier: nextSupplier || undefined,
        supplierPricing: supplierPricing?.map(serializeHardwareSupplierPriceForFirestore),
        updatedAt: Timestamp.now(),
    });
    await updateDoc(docRef, cleaned as any);
}

/**
 * Delete a hardware item
 */
export async function deleteHardwareItem(id: string): Promise<void> {
    const docRef = doc(db, HARDWARE_COLLECTION, id);
    await deleteDoc(docRef);
}

/**
 * Subscribe to real-time updates for hardware items
 */
export function subscribeToHardwareItems(
    callback: (items: HardwareItem[]) => void,
    onError?: (error: Error) => void
): Unsubscribe {
    const q = query(
        collection(db, HARDWARE_COLLECTION),
        orderBy('category')
    );

    return onSnapshot(
        q,
        (snapshot) => {
            const items: HardwareItem[] = snapshot.docs.map((d) =>
                parseHardwareItemFromFirestore(d.id, d.data() as Record<string, any>),
            );
            callback(items);
        },
        (error) => {
            console.error('[MaterialsDB] Hardware subscription error:', error);
            if (onError) onError(error);
        }
    );
}
